import React, { useEffect, useState } from 'react';
import { fetchCmdaPrices } from '../services/cmdaPrices';

const COLORS = {
  bg: '#F4F1EB',
  card: '#FBFAF7',
  text: '#243746',
  accent: '#1F6F78',
};

const MarketPrices = () => {
  const [items, setItems] = useState([]);
  const [sourceDate, setSourceDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const { items: data, date } = await fetchCmdaPrices();
        if (!cancelled) {
          setItems(data);
          setSourceDate(date || '');
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load prices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const todayLabel = sourceDate || new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen px-4 py-6" style={{ backgroundColor: COLORS.bg, color: COLORS.text }}>
      <div className="max-w-3xl mx-auto">
        <header className="mb-5">
          <h1 className="text-2xl font-bold" style={{ color: COLORS.text }}>
            Today's Vegetable Prices
          </h1>
          <p className="text-sm mt-1" style={{ color: COLORS.accent }}>
            {todayLabel}
          </p>
          <p className="text-xs mt-0.5 text-gray-500">
            Source: CMDA Chennai (Koyambedu Wholesale Market)
          </p>
        </header>

        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24" style={{ color: COLORS.accent }}>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="mt-3 text-sm" style={{ color: COLORS.text }}>Fetching today's prices...</p>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-lg p-4 text-sm" style={{ backgroundColor: '#FBE9E7', color: '#8B2E1F', border: '1px solid #F2C5BD' }}>
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="text-center py-16 text-sm" style={{ color: COLORS.text }}>
            No prices available right now.
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((it, idx) => (
              <div
                key={idx}
                className="rounded-xl shadow-sm p-4 flex flex-col gap-2"
                style={{ backgroundColor: COLORS.card, border: '1px solid rgba(31,111,120,0.12)' }}
              >
                <div>
                  <p className="text-base font-semibold leading-tight" style={{ color: COLORS.text }}>
                    {it.nameEn}
                  </p>
                  {it.nameTa && (
                    <p className="text-sm" style={{ color: COLORS.accent }}>
                      {it.nameTa}
                    </p>
                  )}
                </div>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Min – Max</p>
                    <p className="text-lg font-bold" style={{ color: COLORS.text }}>
                      ₹ {it.minPrice ?? '-'}
                      {it.maxPrice && it.maxPrice !== it.minPrice ? ` – ₹ ${it.maxPrice}` : ''}
                    </p>
                  </div>
                  <span
                    className="text-[11px] font-medium px-2 py-1 rounded-md"
                    style={{ backgroundColor: 'rgba(31,111,120,0.1)', color: COLORS.accent }}
                  >
                    {it.unit}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketPrices;
